/**
 * Family Studio ← real server payloads (#577 / R6.2).
 *
 * Three endpoints back this screen, and each one is narrower than the screen it feeds:
 *
 * | screen section | endpoint | what it actually returns |
 * | --- | --- | --- |
 * | Members | `GET /api/admin/users?includeSelf=true` | `{ success, users: [...] }`, camelCase (`admin.service.js` `getAllUsers`) |
 * | Removed members | `GET /api/admin/users/removed` | `{ success, users: [...] }` with `restorable` (`getRemovedUsers`) |
 * | Display devices | `GET /api/displays` | `{ devices: [...] }` (E37) |
 * | Goals | `GET /api/goals/progress?userId=<id>` | `[{ goalId, goalType, currentValue, targetValue, percentage, … }]` |
 * | Household goal | `GET /api/village` | `{ success, village }`; `village: null` means none was ever set |
 * | Pending invitations | `GET /api/auth/signup/invitations` | `{ success, invitations: [...] }`, never a token (#577 row 8) |
 *
 * Every function here exists to stop the page's own defaults from speaking. `Family.jsx` is
 * presentational and ships the mockup's sample household as defaults, so a prop left `undefined`
 * re-selects a sentence about a family that does not exist. The rule, the same one
 * `villageMapping.js` follows: pass the measured value, or pass nothing and let the panel say
 * nothing — never pass a plausible-looking default.
 *
 * What is NOT here, and why:
 *
 *   · **PIN state.** No endpoint reports whether a member has a PIN. The mockup's member cards
 *     carry PIN dots and a trust-mode note; both are omitted rather than guessed. #576 (R6.1)
 *     owns the identity work that would make them real.
 *   · **Device online state.** `display_tokens` has `last_used_at` and no heartbeat. "Online" is
 *     a claim about right now; the last-seen timestamp is a fact. Only the fact is carried.
 *   · **A device preview image.** Nothing serves one.
 *   · **A household join code.** The share link is the copy link of ONE pending invitation, fetched
 *     when a parent asks for it (decision d-20260924-01), so no mapping here produces one.
 */

/** Goal types the schema actually defines. An unknown type is shown verbatim, never renamed. */
export const GOAL_TYPE_LABELS = Object.freeze({
  weekly_chores: 'Quests each week',
  monthly_chores: 'Quests each month',
  weekly_rewards: 'Pocket money each week',
  monthly_rewards: 'Pocket money each month',
});

/** Goal types that count quests. GoalsTable formats a row as money unless the row says otherwise. */
export const QUEST_COUNT_GOAL_TYPES = new Set(['weekly_chores', 'monthly_chores']);

const isObject = (v) => v !== null && typeof v === 'object';

/**
 * The four role values the server can actually store, shown in the family's own words.
 *
 * `users.role` receives `admin` from signup and `admin` | `member` | `guest` from an accepted
 * invitation (`user_invitations_role_valid`), plus the legacy `user`. None of them says "child".
 */
const ROLE_LABELS = Object.freeze({
  admin: 'Parent',
  user: 'Member',
  member: 'Member',
  guest: 'Guest',
});
const nonEmpty = (v) => (typeof v === 'string' && v.trim() !== '' ? v : undefined);

/**
 * `{ success, users }` → the `members` prop.
 *
 * `includeSelf=true` is the caller's business; this function maps whatever list it is given.
 */
export function toMembers(payload) {
  const users = isObject(payload) && Array.isArray(payload.users) ? payload.users : [];
  return users.filter(isObject).map((u) => {
    const serverRole = String(u.role || '').toLowerCase();
    const isAdmin = serverRole === 'admin';
    // A name is the one field that must never fall back to a placeholder: a card reading
    // "Family member" beside a real avatar is the "3 of 0" defect wearing different clothes.
    //
    // CAMELCASE, because that is what `getAllUsers` returns (`displayName`, `firstName`,
    // `avatarUrl`, `isActive`). The first live version read `display_name` and `is_active`, which
    // the endpoint never sends: every card fell back to the username and no deactivated member ever
    // showed as deactivated (#577 row 7).
    const name = nonEmpty(u.displayName) ?? nonEmpty(u.firstName) ?? nonEmpty(u.username);
    const isActive = u.isActive !== false;
    return {
      id: u.id,
      name,
      // NOT 'kid'. `users` has no child/adult column at all — only `role` and a nullable
      // `birth_date` — and `acceptInvitation` writes the invitation's role straight onto
      // `users.role`, so an ADULT invited as `member` lands here. An earlier version called every
      // non-admin a kid and displayed invited adults as children. A cross-model reviewer found it.
      role: isAdmin ? 'parent' : 'member',
      // Kept verbatim so nothing downstream has to re-guess what the server said.
      serverRole,
      roleLabel: ROLE_LABELS[serverRole] ?? 'Member',
      avatar: nonEmpty(u.avatarUrl),
      // The editor starts from the name the server holds, not the fallback shown on the card.
      displayName: nonEmpty(u.displayName) ?? '',
      isActive,
      // `isActive` is the ONE status the users endpoint reports, so it is the one shown.
      subtitle: isActive ? undefined : 'Deactivated — cannot sign in',
    };
  });
}

/**
 * `GET /api/admin/users/removed` → the removed-members list, or `null` when the read failed.
 *
 * An ANONYMIZED member keeps a `username` of `removed-user-<id>`, a uniqueness artifact the day-30
 * job writes (`admin.service.js`, `getRemovedUsers`). It is never shown as a name, and such a member
 * cannot be restored, so `restorable` decides both.
 */
export function toRemovedMembers(payload) {
  const users = isObject(payload) && Array.isArray(payload.users) ? payload.users : [];
  return users.filter(isObject).map((u) => {
    const restorable = u.restorable === true;
    return {
      id: u.id,
      restorable,
      name: restorable ? (nonEmpty(u.displayName) ?? nonEmpty(u.firstName) ?? nonEmpty(u.username)) : undefined,
    };
  });
}

function shortDate(value) {
  const ms = Date.parse(value);
  if (!Number.isFinite(ms)) return undefined;
  return new Date(ms).toLocaleDateString([], { month: 'short', day: 'numeric' });
}

/** The invitation's role, in the family's words. */
export function inviteRoleLabel(role) {
  return ROLE_LABELS[String(role || '').toLowerCase()] ?? 'Member';
}

/**
 * `GET /api/auth/signup/invitations` → the InviteCard's `pending` rows.
 *
 * `sentLabel` says when the email last went out, and how many times when it was more than once.
 * A date the server did not send is left out rather than invented.
 */
export function toPendingInvites(payload) {
  const list = isObject(payload) && Array.isArray(payload.invitations) ? payload.invitations : [];
  return list.filter((i) => isObject(i) && i.id != null && nonEmpty(i.email)).map((i) => {
    const sent = shortDate(i.sentAt);
    const times = Number.isInteger(i.sendCount) && i.sendCount > 1 ? ` · sent ${i.sendCount} times` : '';
    return {
      id: i.id,
      email: i.email,
      role: i.role,
      expiresAt: i.expiresAt,
      sentLabel: sent ? `sent ${sent}${times}` : '',
    };
  });
}

/**
 * The share row's label for ONE invitation's copy link: who it is for, the seat, and when it stops
 * working (decision d-20260924-01). Without it a bare URL would read like a household code.
 */
export function inviteLinkLabel(invitation) {
  if (!isObject(invitation)) return '';
  const until = shortDate(invitation.expiresAt);
  return [
    `Link for ${invitation.email}`,
    inviteRoleLabel(invitation.role),
    until ? `works until ${until}, once` : 'works once',
  ].join(' · ');
}

/**
 * `GET /api/village` → the household goal: `{ name, pointTarget, revision }`, or `null` when none was set.
 * `undefined` is reserved for "not read", which the container passes when the read failed. `revision` is what a
 * save must name to replace the goal (#583 N1 S4). A server from before S4 sends none and checks none, so 0.
 */
export function toHouseholdGoal(payload) {
  const village = isObject(payload) ? payload.village : undefined;
  if (village === null) return null;
  if (!isObject(village)) return null;
  return {
    name: nonEmpty(village.name) ?? 'Your family’s village project',
    pointTarget: Number.isFinite(Number(village.pointTarget)) ? Number(village.pointTarget) : undefined,
    revision: Number.isInteger(village.revision) ? village.revision : 0,
  };
}

/**
 * E37 `{ devices }` → the `devices` prop (#582).
 *
 * The list used to come from `GET /api/display-tokens`, which carries each token's prefix, eight
 * characters of a bearer credential. E37 leaves it out.
 *
 * A revoked display is dropped rather than listed as a device: revoking is how a family unpairs a
 * screen, so showing it would contradict the action the parent already took.
 */
export function toDevices(payload) {
  const devices = isObject(payload) && Array.isArray(payload.devices) ? payload.devices : [];
  return devices
    .filter((d) => isObject(d) && d.revoked !== true)
    .map((d) => ({
      id: d.id,
      name: nonEmpty(d.name) ?? 'Display',
      lastSeen: nonEmpty(d.lastSeen),
      status: nonEmpty(d.lastSeen) ? 'Paired' : 'Paired — never used yet',
    }));
}

/**
 * members + `{ [userId]: progressList }` → the `goals` prop.
 *
 * One row per NON-admin member, because the table is the kids' savings table. A member with no
 * active goal gets `hasGoal: false`, which is what turns the row's action into "Add" — an empty
 * row is an honest answer, and inventing a target would not be.
 *
 * `percentage`, `currentValue` and `targetValue` are copied only when the payload carried them.
 * A missing percentage rendered as 0% would tell a parent their child has saved nothing.
 */
export function toGoalRows(members, progressByUserId) {
  const list = Array.isArray(members) ? members.filter(isObject) : [];
  const byUser = isObject(progressByUserId) ? progressByUserId : {};
  return list
    .filter((m) => m.role !== 'parent')
    .map((m) => {
      const progress = Array.isArray(byUser[m.id]) ? byUser[m.id] : [];
      const active = progress.find(isObject);
      if (!active) {
        return { id: m.id, name: m.name, avatar: m.avatar, hasGoal: false };
      }
      const label = GOAL_TYPE_LABELS[active.goalType] ?? active.goalType;
      // A quest goal counts quests, so it must not read "$7.00" or "funded" (#577, the dev smoke).
      const quests = QUEST_COUNT_GOAL_TYPES.has(active.goalType);
      const count = active.targetValue == null || active.targetValue === '' ? NaN : Number(active.targetValue);
      return {
        ...(quests && Number.isFinite(count) ? { targetLabel: `${count} ${count === 1 ? 'quest' : 'quests'}` } : {}),
        ...(quests ? { progressLabel: `${m.name ? `${m.name}'s ` : ''}${label} goal${typeof active.percentage === 'number' ? `: ${Math.round(active.percentage)}% done` : ''}` } : {}),
        id: m.id,
        name: m.name,
        avatar: m.avatar,
        hasGoal: true,
        // What the editor needs to change or remove THIS goal (#577 row 9).
        goalId: active.goalId,
        goalType: active.goalType,
        goal: label,
        target: active.targetValue,
        value: typeof active.currentValue === 'number' ? active.currentValue : undefined,
        percent: typeof active.percentage === 'number' ? active.percentage : undefined,
      };
    });
}
